import dayjs from "dayjs";
import Holidays, { HolidaysTypes } from "date-holidays";
import {
  SupportedCountries,
  SupportedLanguages,
} from "@/enums/internationalization";
import { Holiday, UnavailabilityPeriod } from "@/types/org";

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;
export const MONTH = 30 * DAY;
export const YEAR = 365 * DAY;

export const GetHolidaysFor365Days = (
  country: SupportedCountries,
  lang: SupportedLanguages
): HolidaysTypes.Holiday[] => {
  let hd: Holidays;
  try {
    hd = new Holidays(country, {
      languages: lang,
      types: ["public"],
    });
  } catch {
    return [];
  }

  // Get this year's and next year's holidays
  const thisYear = new Date().getFullYear();
  const nextYear = thisYear + 1;

  const holidays = [...hd.getHolidays(thisYear), ...hd.getHolidays(nextYear)];

  // Filter out the holidays that are outside of the 365 days
  // Start from the current date (normalized to start of day in UTC to avoid timezone issues)
  const now = new Date();
  const startDate = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );
  const endDate = new Date(startDate.getTime() + 365 * DAY);

  // Filter the holidays that are within the 365 days
  // Use holiday.start (Date object) for reliable comparisons with date-fns functions
  // Include holidays on or after startDate and before endDate
  const filteredHolidays = holidays.filter((holiday) => {
    // Normalize holiday.start to start of day for comparison
    const holidayStart = new Date(
      Date.UTC(
        holiday.start.getUTCFullYear(),
        holiday.start.getUTCMonth(),
        holiday.start.getUTCDate(),
        0,
        0,
        0,
        0
      )
    );
    // Use isAfter or isEqual to include holidays on the start date, isBefore for end date (exclusive)
    const holidayDayjs = dayjs(holidayStart);
    const startDayjs = dayjs(startDate);
    const endDayjs = dayjs(endDate);
    return (
      (holidayDayjs.isSame(startDayjs) || holidayDayjs.isAfter(startDayjs)) &&
      holidayDayjs.isBefore(endDayjs)
    );
  });

  // Return the holidays
  return filteredHolidays;
};

// Helper function to convert a date to YYYY-MM-DD string (using UTC to avoid timezone issues)
const toDateString = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to convert a date string to YYYY-MM-DD format
const normalizeDateString = (dateString: string): string => {
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Otherwise, parse as ISO string and convert to YYYY-MM-DD
  return toDateString(new Date(dateString));
};

// Returns a list of holidays with no overlap with the current holidays
export const GetUnusedHolidays = (
  currentHolidays: UnavailabilityPeriod[],
  holidays: HolidaysTypes.Holiday[]
): HolidaysTypes.Holiday[] => {
  // If no current holidays, return all holidays
  if (!currentHolidays || currentHolidays.length === 0) {
    return holidays;
  }

  // Filter holidays that don't overlap with any UnavailabilityPeriod
  return holidays.filter((holiday) => {
    // Get the holiday date as YYYY-MM-DD string
    const holidayDateStr = toDateString(holiday.start);

    // Check if this holiday overlaps with any UnavailabilityPeriod
    const hasOverlap = currentHolidays.some((period) => {
      const periodStartStr = normalizeDateString(period.startDate);
      const periodEndStr = normalizeDateString(period.endDate);

      // Compare as strings (YYYY-MM-DD constraint format allows lexicographic comparison)
      // holidayDateStr >= periodStartStr && holidayDateStr <= periodEndStr
      return holidayDateStr >= periodStartStr && holidayDateStr <= periodEndStr;
    });

    // Return holidays that don't have overlap
    return !hasOverlap;
  });
};

export const GetUnusedHolidaysFor365Days = (
  country: SupportedCountries,
  lang: SupportedLanguages,
  currentHolidays: UnavailabilityPeriod[]
): Holiday[] => {
  const holidays = GetUnusedHolidays(
    currentHolidays,
    GetHolidaysFor365Days(country, lang)
  );

  return holidays.map((holiday) => ({
    name: holiday.name,
    startDate: holiday.start.toISOString().split("T")[0] + "T00:00:00",
    endDate: dayjs(holiday.end).subtract(1, "day").toISOString().split("T")[0] + "T23:59:59",
  }));
};

const toDateKey = (value: string | Date): string => {
  if (typeof value === "string") {
    return value.split("T")[0];
  }
  return toDateString(value);
};

/**
 * Yearly reminder on the inbound holidays section: visible Dec 1–Jan 15,
 * hidden after Jan 15 or once a holiday starting after Jan 7 of the new year exists.
 */
export const shouldShowYearlyHolidayReminder = (
  now: Date,
  holidays: UnavailabilityPeriod[]
): boolean => {
  const month = now.getMonth();
  const day = now.getDate();
  const inWindow = (month === 11 && day >= 1) || (month === 0 && day <= 15);
  if (!inWindow) {
    return false;
  }

  const targetYear = month === 11 ? now.getFullYear() + 1 : now.getFullYear();
  const yearPrefix = `${targetYear}-`;
  const cutoffKey = `${targetYear}-01-07`;

  return !holidays.some((holiday) => {
    const startKey = toDateKey(holiday.startDate);
    return startKey.startsWith(yearPrefix) && startKey > cutoffKey;
  });
};
